import IconSearch from '@apollo/icons/default/IconSearch.svg';
import IconWarning from '@apollo/icons/default/IconWarning.svg';
import IconInfo from '@apollo/icons/small/IconInfo.svg';
import {
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Group,
  Input,
  SortOrder,
  Table,
  TableContainer,
  TableSortButton,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@apollo/orbit';
import React, { useEffect, useMemo, useState } from 'react';

import { Icon as IconPlanetWithStars } from 'src/assets/icon-planet-with-stars.svg';

import { HTTPStatusBadge } from '../components/HTTPStatusBadge';

export interface Request {
  url: string;
  path: string;
  method: string;
  status: number | undefined;
  idx: number;
  id: number;
  selectionErrorsCount: number;
}

export interface RequestListProps {
  requests: Request[];
  onRowClick: (
    rowIdx: Request['idx'],
    setShowMappingErrorFirst?: boolean,
  ) => void;
}

export const RequestList = ({ requests, onRowClick }: RequestListProps) => {
  const [filter, setFilter] = useState('');
  const [sortBy, setSortBy] = useState<'idx' | 'selectionErrorsCount'>('idx');
  const [sortOrder, setSortOrder] = useState<SortOrder>();

  useEffect(() => {
    setFilter('');
  }, [requests]);

  const sortedRows = useMemo(() => {
    return !sortOrder
      ? requests
      : [...requests].sort((a, b) => {
          const [item, compareTo] = sortOrder === 'asc' ? [a, b] : [b, a];
          return comparators[sortBy](item, compareTo);
        });
  }, [requests, sortBy, sortOrder]);

  const lowercaseFilter = filter.toLowerCase();
  const filteredSortedRows = sortedRows.filter((row) => {
    return Object.values(row).some((value) => {
      const stringValue = typeof value === 'string' ? value : `${value}`;
      return stringValue.toLowerCase().indexOf(lowercaseFilter) > -1;
    });
  });

  return requests.length ? (
    <Group
      orientation="vertical"
      gap={6}
      justify="start"
      align="start"
      className="size-full overflow-hidden"
    >
      <FormControl>
        <FormLabel className="sr-only">Search requests</FormLabel>
        <Input
          onChange={({ target }) => {
            const { value } = target;
            setFilter(value);
          }}
          placeholder="Search"
          leftElement={<IconSearch />}
          value={filter}
        />
      </FormControl>
      <Card className="w-full pb-2">
        <CardBody>
          <TableContainer>
            <Table
              variant="striped"
              size="condensed"
              isInteractive
              sortBy={sortBy}
              onRequestSort={({
                sortOrder: nextSortOrder,
                sortBy: nextSortBy,
              }) => {
                setSortBy(nextSortBy);
                setSortOrder(nextSortOrder);
              }}
            >
              <colgroup>
                <col width="5%" data-column-id="id" />
                <col width="60%" data-column-id="url" />
              </colgroup>
              <Thead>
                <Tr>
                  <Th>
                    <Group>
                      ID
                      <TableSortButton attribute="idx" />
                    </Group>
                  </Th>
                  <Th>Path</Th>
                  <Th>Status</Th>
                  <Th>Method</Th>
                  <Th>
                    <Group>
                      <TableSortButton
                        attribute="selectionErrorsCount"
                        defaultSortOrder="desc"
                      />
                      Warnings
                      <Tooltip label="Total mapping warnings">
                        <IconInfo tabIndex={0} className="text-icon-primary" />
                      </Tooltip>
                    </Group>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredSortedRows.map(
                  ({
                    idx,
                    id,
                    url,
                    path,
                    method,
                    status,
                    selectionErrorsCount,
                  }) => (
                    <Tr
                      key={id}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRowClick(idx);
                      }}
                      className="cursor-pointer"
                    >
                      <Td>{id}</Td>
                      <Td className="max-w-0 truncate">
                        <Tooltip label={url} className="break-all">
                          <span className="cursor-auto">{path}</span>
                        </Tooltip>
                      </Td>
                      <Td>
                        <HTTPStatusBadge status={status} showLabel={false} />
                      </Td>
                      <Td>
                        <span className="cursor-auto">{method}</span>
                      </Td>
                      <Td className="text-center">
                        {selectionErrorsCount ? (
                          <Tooltip label="View mapping warnings">
                            <Button
                              size="sm"
                              type="button"
                              variant="secondary"
                              leftIcon={
                                <IconWarning className="!text-icon-warning" />
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                onRowClick(idx, true);
                              }}
                            >
                              {selectionErrorsCount}
                            </Button>
                          </Tooltip>
                        ) : undefined}
                      </Td>
                    </Tr>
                  ),
                )}
              </Tbody>
            </Table>
            {!filteredSortedRows.length && (
              <EmptyLayout
                message="No matching requests found"
                className="my-12"
              />
            )}
          </TableContainer>
        </CardBody>
      </Card>
    </Group>
  ) : (
    <EmptyLayout message="No requests to display" className="my-12" />
  );
};

const comparators: Record<
  'idx' | 'selectionErrorsCount',
  (a: Request, b: Request) => number
> = {
  idx: (a, b) => a.idx - b.idx,
  selectionErrorsCount: (a, b) =>
    a.selectionErrorsCount - b.selectionErrorsCount,
};

const EmptyLayout = ({
  message,
  className,
}: {
  message: string;
  className?: string;
}) => (
  <Group
    orientation="vertical"
    justify="center"
    align="center"
    gap={2}
    className={className}
  >
    <IconPlanetWithStars />
    <Text>{message}</Text>
  </Group>
);
